import { useEffect, useState, useRef, useContext } from 'react';
import { PopoverHeader, PopoverBody, PopoverFooter } from '@src/components/popover-content/PopoverContent';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from "@/components/ui/textarea"
import { CustomTooltip } from '@src/components/tooltip/Tooltip';
import { Button } from "@/components/ui/button";
import { Icon } from '@src/components/icon/Icon';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form } from '@/components/ui/form';
import Draggable, { DraggableEventHandler } from 'react-draggable';
import cn from 'classnames';
import { FetchContext } from '@src/scenario-editor/providers/fetch.context.tsx';
import { v4 as uuidv4 } from 'uuid';

const MAX_ALTERNATIVES = 20;

const FormSchema = z.object({
  alternatives: z.array(
    z.object({
      value: z.string(),
      id: z.string()
    })
  ).max(MAX_ALTERNATIVES, `Maximum ${MAX_ALTERNATIVES} alternatives allowed`)
});

interface TextAreaValue {
  value: string;
  id: string;
}
interface FormValues {
  alternatives: TextAreaValue[];
}
interface AlternativesComponentProps {
  originalValue: string;
  alternativeValues: string[];
  onValuesChange?: (values: string[]) => void;
  onClose: () => void;
}
export const AlternativesComponent = ({
  originalValue,
  alternativeValues=[],
  onValuesChange,
  onClose
}: AlternativesComponentProps) => {
  const [popoverCoordinates, setPopoverCoordinates] = useState({ x: -400, y: 0 });
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [fetchingState, setFetchingState] = useState<Record<string, boolean>>({});


  const context = useContext(FetchContext)!;

  if (!context) {
    throw new Error('FetchContext is not provided');
  }

  const defaultValues: FormValues = {
    alternatives: alternativeValues.map((value) => ({ value, id: uuidv4() }))
  }
  const textareaRefs = useRef<HTMLTextAreaElement[]>([]);
  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues,
  });
  const { fields, remove, prepend } = useFieldArray({
    control: form.control,
    name: 'alternatives',
  });
  const defaultCoordinates = {
    x: popoverCoordinates.x,
    y: popoverCoordinates.y,
  }

  useEffect(() => {
    if (alternativeValues.length === 0) {
      const fields = form.getValues().alternatives;
      if (fields.length === 0) {
        prepend({ value: '', id: uuidv4() });
      }
      setTimeout(() => {
        // firstTextareaRef.current?.focus();
        textareaRefs.current[0]?.focus();
      }, 0);
    }

    return () => {
      setTimeout(() => {
        const filteredValues = (form.getValues().alternatives ?? []).filter(value => value && value.value.trim() !== '');
        onValuesChange?.(filteredValues.map((value) => value.value.trim()));
      }, 100);
    }
  }, []);

  useEffect(() => {
    // INFO: keep this  empty logic for now (in case of track form values)
    const subscription = form.watch(() => {

      return () => {
        subscription.unsubscribe();
      }
    })
  }, [form.handleSubmit, form.watch]);

  useEffect(() => {
    // adjust each textarea height to fit the content
    textareaRefs.current.forEach((textarea) => {
      if (textarea) {
        textarea.style.height = `10px`;
        const contentHeight = textarea.scrollHeight;
        textarea.style.height = `${contentHeight}px`;
      }
    });
  }, [fields]);

  const onCloseHandler = () => {
    onClose();
  }

  const dragHandler: DraggableEventHandler = (_,data) => {
    setPopoverCoordinates({ x: data.x, y: data.y });
  }

  const aiClickHandler = (index: number) => {
    if (context.fetchAlternatives) {
      const currentValue = form.getValues().alternatives[index];
      setFetchingState({ ...fetchingState, [index]: true });
      context.fetchAlternatives(originalValue, form.getValues().alternatives.map(item => item.value)).then((payload) => {
        const alternatives = payload.data.alternativeMessages;
        form.setValue(`alternatives.${index}`, { ...currentValue, value: alternatives[0] });
        setFetchingState({ ...fetchingState, [index]: false });
      }).catch((error) => {
        console.error('fetchAlternatives error', error);
        setFetchingState({ ...fetchingState, [index]: false });
        form.setValue(`alternatives.${index}`, currentValue);
      });
    }
  }

  return (
    <Draggable
      onDrag={dragHandler}
      handle=".popover-alternatives-header"
      position={defaultCoordinates}
    >
      <div className="alternatives-wrapper shadow-lg rounded-md fixed top-0 left-0 w-full h-auto bg-white z-10 -translate-x-full -mx-2">
        <PopoverHeader
          title="Alternative messages"
          className="popover-alternatives-header cursor-move"
          actions={
            <CustomTooltip content="Close alternatives">
              <button tabIndex={-1} onClick={onCloseHandler}>
                <Icon type="close" />
              </button>
            </CustomTooltip>
          }
        />
        <PopoverBody>
          <div
            className="text-sm p-4 max-h-[70vh] overflow-y-auto overflow-x-hidden"
          >
            <Form {...form}>
              {fields.map((field, index) => {
                return (
                  <div key={field.id} className="grid grid-cols-[1fr_1rem] border-separator border-b pb-2 last:border-b-0">
                    <div key={`original-value-controller`} className="py-2 pt-4">
                      <Controller
                        name={`alternatives.${index}`}
                        control={form.control}
                        render={({ field }) => {
                          const { onChange, value } = field;

                          return (
                            <div>
                              {(fetchingState[index]) && (
                                <div className="flex flex-col justify-center pl-3 pt-2 absolute z-10">
                                  <Skeleton className="h-3 w-[250px] bg-gray-300 my-1" />
                                  <Skeleton className="h-3 w-[250px] bg-gray-300 my-1" />
                                  <Skeleton className="h-3 w-[200px] bg-gray-300 my-1" />
                                </div>
                              )}
                              <Textarea
                                ref={(el) => {
                                  textareaRefs.current[index] = el!;
                                }}
                                variant="borderless"
                                className={cn(
                                  "pr-14 resize-none",
                                  {
                                    'opacity-0 cursor-not-allowed': fetchingState[index],
                                  }
                                )}
                                value={value.value}
                                placeholder="Add message"
                                onChange={(e) => {
                                  const currentValue = form.getValues().alternatives[index];
                                  onChange({ ...currentValue, value: e.target.value });
                                }}
                                onMouseEnter={() => {
                                  setHoveredIndex(index);
                                }}
                                onMouseLeave={() => setHoveredIndex(null)}
                                onFocus={() => {
                                  setTimeout(() => setFocusedIndex(index), 100);
                                }}
                                onBlur={(e) => {
                                  setTimeout(() => {
                                    setFocusedIndex(null)
                                    onValuesChange?.((form.getValues().alternatives ?? []).map((value) => value.value.trim()));
                                  }, 100);
                                  // resize the textarea to fit the content
                                  e.target.style.height = `10px`;
                                  const contentHeight = e.target.scrollHeight;
                                  e.target.style.height = `${contentHeight}px`;
                                }}
                              />
                            </div>
                          );
                        }}
                      />
                    </div>
                    <div className={cn(
                      '-translate-x-14 pt-2',
                      {
                        'h-0 overflow-hidden': focusedIndex !== index && hoveredIndex !== index
                      }
                    )}>
                      <div className="py-2 pt-4 flex absolute z-10" onMouseEnter={() => setHoveredIndex(index)}>
                        <CustomTooltip content="Generate with AI">
                          <button
                            key={`ai-button-${index}`}
                            className="text-input"
                            onClick={() => {
                              aiClickHandler(index)
                            }}
                          >
                            <Icon type="ai"/>
                          </button>
                        </CustomTooltip>
                        <CustomTooltip content="Delete">
                          <button
                            key={`delete-button-${index}`}
                            className="text-input"
                            onClick={() => {
                              remove(index);
                              setTimeout(() => {
                                onValuesChange?.((form.getValues().alternatives ?? []).map((value) => value.value.trim()));
                                if (fields.length === 1) {
                                  prepend({ value: '', id: uuidv4() });
                                  setTimeout(() => {
                                    textareaRefs.current[0]?.focus();
                                  }, 100);
                                }
                              }, 100)
                          }}>
                            <Icon type="delete"/>
                          </button>
                        </CustomTooltip>
                      </div>
                    </div>
                  </div>
                );
              })}
            </Form>
          </div>
        </PopoverBody>
        <PopoverFooter>
          <Button
            variant="ghost"
            role="combobox"
            // disabled={Object.keys(formErrors?.alternatives ?? {}).length > 0}
            onClick={() => {
              prepend({ value: '', id: uuidv4() });
              // focus on the newly added textarea
              setTimeout(() => {
                textareaRefs.current[0]?.focus();
              }, 0);
            }}
          >
            <Icon type="plus" />
            <span
              className="text-label-foreground font-light"
            >
              Add alternative
            </span>
          </Button>
        </PopoverFooter>
      </div>
    </Draggable>
  );
}
